import { CashBackDisclosure } from '@fruitful/compliance';
import { CashBackPolicy } from '@fruitful/types';
import { router } from 'expo-router';
import React, { Fragment } from 'react';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    FullScreenSpinner,
    H5Regular,
    Icon,
    InfoButton,
    isWeb,
    MessageCircleIcon,
    SafeAreaView,
    ScrollView,
    SmoothieScrollView,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { DetailsTileForAccount } from '../../banking/components/details-tile/DetailsTileForAccount';
import { DetailsTileForCard } from '../../banking/components/details-tile/DetailsTileForCard';
import { BankGeneralDisclosureFootnote } from '../../banking/components/Disclosures';
import useGetAllBankAccountDetailsFromGlobalState from '../../banking/hooks/useGetAllBankAccountDetailsFromGlobalState';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { getMoneyMapBillsRuleAccounts } from '../../common/utils/bankingSwitchUtil';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import useGetActiveMoneyMap from '../../roadmap/hooks/useGetActiveMoneyMap';
import { ROUTING_NUMBER_FAQ_EXPAND_PARAM } from '../constants/bankingSwitchFaq';
import { SwitchableBills } from './SwitchableBills';

const SwitchBills = () => {
    const user = useAuthedUser();
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('BankingSwitch/SwitchBills');

    const cashBackPolicyUnrestricted =
        user.membership?.product?.banking.cashBackPolicy ===
        CashBackPolicy.UNRESTRICTED;

    // TODO! This isn't the money map we want! It's not the currently IMPLEMENTED money map
    const { isLoadingMoneyMap, moneyMap } = useGetActiveMoneyMap();
    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetailsFromGlobalState();

    if (isLoadingMoneyMap || isLoadingBankAccountDetails) {
        return <FullScreenSpinner />;
    }

    const accountsAndCardsToRender =
        moneyMap != null
            ? getMoneyMapBillsRuleAccounts(moneyMap, bankAccountDetails, user)
            : [];

    return (
        <SafeAreaView
            edges={['top', 'left', 'right']}
            style={{
                flex: 1,
            }}
        >
            <SmoothieScrollView>
                <YStack gap={12} alignSelf="center">
                    <XStack justifyContent="space-between" alignItems="center">
                        <BackButton iconType={BackButtonIconType.ARROW_LEFT} />
                        <InfoButton
                            onPress={() => {
                                router.navigate({
                                    pathname:
                                        '/banking-switch/paying-bills-faq',
                                });
                            }}
                        />
                    </XStack>
                    <H5Regular paddingLeft={8}>Switch your bills</H5Regular>
                    <BodyMediumRegular color="$gray1000" padding={8}>
                        Log in to each company&apos;s website and update your
                        payment method with the card or account info below to
                        start paying your bills from Fruitful.{' '}
                        <BodyMediumRegular
                            color="$green1100"
                            textDecorationLine="underline"
                            onPress={() => {
                                router.navigate({
                                    pathname:
                                        '/banking-switch/paying-bills-faq',
                                    params: {
                                        expand: ROUTING_NUMBER_FAQ_EXPAND_PARAM,
                                    },
                                });
                            }}
                            hoverStyle={{ cursor: 'pointer' }}
                        >
                            Routing number not recognized?
                        </BodyMediumRegular>
                    </BodyMediumRegular>
                    {accountsAndCardsToRender.length > 0 && (
                        <View height={150}>
                            <ScrollView
                                horizontal={true}
                                showsHorizontalScrollIndicator={isWeb}
                                showsVerticalScrollIndicator={isWeb}
                                contentContainerStyle={{
                                    paddingBottom: 8,
                                    height: 150,
                                    ...(isWeb ? { width: 300 } : {}),
                                }}
                            >
                                <XStack gap={16}>
                                    {accountsAndCardsToRender.map(
                                        ({ card, bankAccount }, i) => (
                                            <Fragment
                                                key={`${bankAccount?.bankAccountId ?? i}`}
                                            >
                                                {card && (
                                                    <DetailsTileForCard
                                                        card={card}
                                                        accountName={
                                                            bankAccount?.nickname
                                                        }
                                                    />
                                                )}
                                                <DetailsTileForAccount
                                                    account={bankAccount}
                                                />
                                            </Fragment>
                                        ),
                                    )}
                                </XStack>
                            </ScrollView>
                        </View>
                    )}
                    <XStack
                        backgroundColor="$accent1000"
                        gap={12}
                        padding={16}
                        borderRadius={12}
                        alignItems="center"
                    >
                        <View width={24}>
                            <Icon
                                icon={MessageCircleIcon}
                                color="$grayAccentContrast"
                                size={24}
                            />
                        </View>
                        {cashBackPolicyUnrestricted ? (
                            <BodyMediumRegular
                                flex={1}
                                color="$grayAccentContrast"
                            >
                                If you use your Fruitful Card you will earn 2%
                                cash back¹ on your bills.{' '}
                            </BodyMediumRegular>
                        ) : (
                            <BodyMediumRegular
                                flex={1}
                                color="$grayAccentContrast"
                            >
                                If you use your Fruitful Card you will earn up
                                to 2% cash back¹ on your bills.{' '}
                            </BodyMediumRegular>
                        )}
                    </XStack>
                    <SwitchableBills />
                    <BankGeneralDisclosureFootnote />
                    <YStack marginBottom={40}>
                        <CashBackDisclosure
                            superscript={1}
                            color="$gray900"
                            titleColor="$gray1000"
                        />
                    </YStack>
                </YStack>
            </SmoothieScrollView>
        </SafeAreaView>
    );
};

export default SwitchBills;
